import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface KeyStatus {
  configured: boolean;
  updated_at: string | null;
  certificate: { fingerprint_sha256: string; expires_at: string; subject: string } | null;
  matches_certificate: boolean | null;
  legacy_key_present: boolean;
}

async function invoke<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("qz-signing", { body });
  if (error) {
    let msg = error.message;
    try {
      const ctx = (error as { context?: Response }).context;
      const parsed = ctx ? await ctx.json() : null;
      if (parsed?.error) msg = parsed.error;
    } catch {
      /* keep default message */
    }
    throw new Error(msg);
  }
  return (typeof data === "string" ? JSON.parse(data) : data) as T;
}

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" });

/**
 * Write-only management of the company's QZ private key. The key is never
 * loaded into the browser; only status, fingerprint and expiry are shown.
 */
export function QzSigningKeyPanel({
  companyId,
  certificateDraft,
  onCertificateSaved,
}: {
  companyId: string;
  certificateDraft: string;
  onCertificateSaved?: () => void;
}) {
  const [status, setStatus] = useState<KeyStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [pem, setPem] = useState("");
  const [alsoCert, setAlsoCert] = useState(false);
  const [saving, setSaving] = useState(false);
  const migrated = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let s = await invoke<KeyStatus>({ action: "key_status", company_id: companyId });
      if (s.legacy_key_present && !migrated.current) {
        migrated.current = true;
        const r = await invoke<{ migrated: boolean }>({ action: "migrate_legacy_key", company_id: companyId });
        if (r.migrated) toast.success("Key moved to secure storage");
        s = await invoke<KeyStatus>({ action: "key_status", company_id: companyId });
      }
      setStatus(s);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not load key status");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const close = () => {
    setPem("");
    setAlsoCert(false);
    setOpen(false);
  };

  const submit = async () => {
    setSaving(true);
    try {
      await invoke({
        action: "set_key",
        company_id: companyId,
        private_key: pem,
        ...(alsoCert && certificateDraft.trim() ? { certificate: certificateDraft.trim() } : {}),
      });
      toast.success("Private key replaced");
      if (alsoCert) onCertificateSaved?.();
      close();
      await load();
    } catch (e) {
      setPem("");
      toast.error(e instanceof Error ? e.message : "Could not replace key");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label>QZ Tray private key</Label>
      <div className="rounded-md border border-border p-3 space-y-1.5 text-sm">
        {loading ? (
          <div className="animate-pulse h-4 w-48 bg-muted rounded" />
        ) : (
          <>
            <div className="flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-muted-foreground" />
              {status?.configured ? (
                <span className="text-foreground">
                  Private key configured
                  {status.updated_at ? ` · updated ${fmtDate(status.updated_at)}` : ""}
                </span>
              ) : (
                <span className="text-muted-foreground">Not configured</span>
              )}
            </div>
            {status?.certificate ? (
              <div className="text-xs text-muted-foreground space-y-0.5">
                <div>
                  Certificate fingerprint (SHA-256):{" "}
                  <span className="font-mono break-all">{status.certificate.fingerprint_sha256}</span>
                </div>
                <div>Certificate expires {fmtDate(status.certificate.expires_at)}</div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No valid certificate saved yet.</p>
            )}
            {status?.configured && status.matches_certificate === false && (
              <p className="flex items-center gap-1.5 text-xs text-destructive">
                <AlertTriangle className="w-3.5 h-3.5" />
                The stored key does not match the saved certificate. Replace both together.
              </p>
            )}
          </>
        )}
        <Button variant="outline" size="sm" className="mt-1" onClick={() => setOpen(true)}>
          Replace private key
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        The private key is stored encrypted on the server and used only for signing. It can't be
        viewed or downloaded — only replaced.
      </p>

      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Replace private key</DialogTitle>
            <DialogDescription>
              Paste the RSA private key (PEM) that belongs to the certificate. The old key is
              overwritten and can't be recovered.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={8}
            value={pem}
            onChange={(e) => setPem(e.target.value)}
            placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
            className="font-mono text-xs"
            autoComplete="off"
            spellCheck={false}
          />
          <div className="flex items-center gap-2">
            <Checkbox
              id="qz_also_cert"
              checked={alsoCert}
              onCheckedChange={(v) => setAlsoCert(v === true)}
              disabled={!certificateDraft.trim()}
            />
            <Label htmlFor="qz_also_cert" className="text-sm font-normal">
              Also save the certificate from the field above
            </Label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={saving || !pem.trim()}>
              {saving ? "Saving…" : "Replace key"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
